// Verwaltung › Turnier-Einstellungen (/dashboard/turnier-einstellungen)
//
// Nach ~/Documents/TDA-Muster/Muster_Turnier_Einstellungen.html, Variante A
// (Entscheidung des Inhabers 23.10.2026): links einklappbare Gruppen mit
// Status je Eintrag, Mitte Bearbeiten, rechts Zusammenfassung und Speichern;
// Handy: Liste → Bereich. Alle Bereiche werden zusammen gespeichert.
//
// Quellen: Zeitplan /api/schedule/zeitplan-einstellungen (Standardzeile bzw.
// je Turnier, mit `stand` für die Konflikterkennung), Standard-Wertung
// /api/wertung/einstellungen (nur Plattform-Admin ändert), Vorlagen
// /api/ausschreibung/vorlagen (eigener Verband; Plattform wählt).
// Felder ohne Wirkung sind ausgeblendet: lib/einstellungenWirkung.js.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '../../components/common/Toast';
import '../../styles/vereine.css';
import '../../styles/turniereinstellungen.css';
import { apiJson } from '../Club/vereine/vereinHilfen';
import { Dialog } from '../Club/vereine/VereinTeile';
import {
  BASIS_ID, VORLAGEN, klon, zeitplanAus, gruppen as baueGruppen, alleIds, gruppeVon, titelVon,
  geaenderteFelder, alleFehler, fehlerVon, status, zeitplanUnterschiede,
} from './einstellungenModell';
import { Ico, Gruppenliste, EditorInhalt, EditorKopf } from './EinstellungenTeile';
import { ALT_ZEILEN, platzDreiImModus } from '../../lib/kampfRegeln';

const istPlattformAdmin = () => {
  try {
    const u = JSON.parse(localStorage.getItem('adminData') || 'null')?.user;
    return !!u && u.verband_id_tenant == null;
  } catch { return false; }
};
const datumDe = (d) => (d ? new Date(d).toLocaleDateString('de-DE') : 'ohne Datum');
const zeitDe = (stand) => (stand ? new Date(stand * 1000).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : null);
const STATUS_TURNIER = { Entwurf: 'Entwurf', Aktiv: 'Aktiv', anmeldung_geschlossen: 'Anmeldung geschlossen', Laufend: 'Laufend', Abgeschlossen: 'Abgeschlossen' };

// Klicks, die die Seite verlassen (Kopfleiste, Menü, Links): bei offenen
// Änderungen erst fragen. Theme- und Sprachknopf bleiben frei.
const WEG = 'a[href]:not([target="_blank"]), .header-pill-btn--red, .header-pill-btn--admin, .dropdown-item, .verband-switcher-item, .md-zwei-faktor-hinweis button, .sidebar a, .sidebar button';

export default function TurnierEinstellungenPage() {
  const navigate = useNavigate();
  const plattform = useMemo(istPlattformAdmin, []);
  const wurzel = useRef(null);

  const [turniere, setTurniere] = useState([]);
  const [eigene, setEigene] = useState(new Set());
  const [vorgabe, setVorgabe] = useState(null);          // Standardzeile (für Kampfrichter, Zurücksetzen)
  const [scope, setScope] = useState('std');
  const [d, setD] = useState(null);                       // { zp, wertung, vorlagen }
  const [laedt, setLaedt] = useState(true);
  const [ladeFehler, setLadeFehler] = useState('');
  const [verbaende, setVerbaende] = useState([]);
  const [verband, setVerband] = useState('');
  const [sel, setSel] = useState('z:Formen');
  const [offen, setOffen] = useState(() => new Set(['zeitplan']));
  const [filter, setFilter] = useState('alle');
  const [hd, setHd] = useState('liste');
  const [dialog, setDialog] = useState(null);
  const [speichert, setSpeichert] = useState(false);
  const [speicherFehler, setSpeicherFehler] = useState('');
  const frei = useRef(false);

  // Kampf um Platz 3 im Zeitplan: Turniermodus des gewählten Turniers, für die
  // Vorgabe der Vorschlag der Eingabemaske (lib/kampfRegeln.js platzDreiImModus)
  const platzDrei = platzDreiImModus(scope === 'std' ? null : turniere.find((t) => t.turnier_id === scope)?.default_tournament_mode);
  const ctx = { scope, plattform, kannZeitplan: scope !== 'std' || plattform, platzDrei };

  /* ═══ Laden ═══ */
  const ladeWertung = useCallback(async () => {
    const r = await apiJson('/api/wertung/einstellungen');
    const zeilen = (r.data || []).slice().sort((a, b) => (a.wertungs_system === b.wertungs_system ? a.division_type.localeCompare(b.division_type, 'de') : a.wertungs_system === 'bracket' ? -1 : 1));
    const m = Object.fromEntries(zeilen.map((z) => [z.division_type, { ...z }]));
    return { werte: klon(m), basis: klon(m), reihenfolge: zeilen.map((z) => z.division_type) };
  }, []);
  const ladeVorlagen = useCallback(async (vid) => {
    if (plattform && !vid) return null;
    const r = await apiJson(`/api/ausschreibung/vorlagen${plattform ? `?verband_id=${vid}` : ''}`);
    const t = Object.fromEntries(VORLAGEN.map((v) => [v.k, r.data?.[v.k] || '']));
    return { werte: { ...t }, basis: { ...t }, verband_name: r.data?.verband_name || '', verband_id: r.data?.verband_id ?? vid };
  }, [plattform]);
  const ladeZeitplan = useCallback(async (sc) => {
    const r = await apiJson(sc === 'std' ? '/api/schedule/zeitplan-einstellungen/default' : `/api/schedule/zeitplan-einstellungen/${sc}`);
    const z = zeitplanAus(r.data);
    return { werte: klon(z), basis: klon(z), quelle: r.source, stand: r.data?.stand ?? null, matten: sc === 'std' ? null : Number(r.data?.rings ?? 0), geaendert: r.data?.updated_at || null };
  }, []);

  const ladeScope = useCallback(async (sc, vid) => {
    setLaedt(true); setLadeFehler(''); setSpeicherFehler('');
    try {
      const zp = await ladeZeitplan(sc);
      const [wertung, vorlagen] = sc === 'std'
        ? await Promise.all([ladeWertung(), ladeVorlagen(vid)])
        : [{ werte: {}, basis: {}, reihenfolge: [] }, null];
      setD({ zp, wertung, vorlagen });
      setScope(sc);
      if (sc === 'std') {
        const std = await apiJson('/api/schedule/zeitplan-einstellungen/default');
        setVorgabe(zeitplanAus(std.data));
      }
    } catch (e) {
      setLadeFehler(e.message);
    } finally { setLaedt(false); }
  }, [ladeZeitplan, ladeWertung, ladeVorlagen]);

  // Erstes Laden: Turniere, Verbände (Plattform), Vorgabe, eigene Turnierwerte
  useEffect(() => {
    let ab = false;
    (async () => {
      try {
        const [t, std] = await Promise.all([apiJson('/api/turniere'), apiJson('/api/schedule/zeitplan-einstellungen/default')]);
        if (ab) return;
        const liste = (t.data || []).slice().sort((a, b) => String(b.datum || '').localeCompare(String(a.datum || '')));
        setTurniere(liste);
        setVorgabe(zeitplanAus(std.data));
        let vid = '';
        if (plattform) {
          const v = await apiJson('/api/verbaende');
          const l = v.data || v.verbaende || [];
          setVerbaende(l);
          const aktiv = localStorage.getItem('activeVerbandId');
          vid = String((l.find((x) => String(x.id) === String(aktiv)) || l[0] || {}).id || '');
          setVerband(vid);
        }
        await ladeScope('std', vid);
        // Welche Turniere haben eigene Zeitplanwerte? (je Turnier eine Abfrage)
        const ergebnis = await Promise.all(liste.map((x) => apiJson(`/api/schedule/zeitplan-einstellungen/${x.turnier_id}`)
          .then((r) => [x.turnier_id, r.source === 'turnier', Number(r.data?.rings ?? 0)]).catch(() => [x.turnier_id, false, null])));
        if (ab) return;
        setEigene(new Set(ergebnis.filter(([, e]) => e).map(([id]) => id)));
        setTurniere((alt) => alt.map((x) => ({ ...x, matten: ergebnis.find(([id]) => id === x.turnier_id)?.[2] })));
      } catch (e) { if (!ab) { setLadeFehler(e.message); setLaedt(false); } }
    })();
    return () => { ab = true; };
  }, [plattform, ladeScope]);

  /* ═══ Ableitungen ═══ */
  const gr = useMemo(() => baueGruppen(scope, (d?.wertung?.reihenfolge || []).map((k) => d.wertung.werte[k])), [scope, d]);
  const ids = useMemo(() => alleIds(gr), [gr]);
  const fehler = useMemo(() => alleFehler(d), [d]);
  const zahlen = useMemo(() => {
    if (!d) return { ok: 0, fehlt: 0, neu: 0, fehler: 0, aenderungen: 0, bereiche: 0 };
    const c = { ...ctx, fehler };
    const s = ids.map((id) => status(id, d, c));
    const aend = ids.map((id) => geaenderteFelder(id, d).length);
    return {
      ok: s.filter((x) => ['ok', 'erbt', 'zu'].includes(x)).length, fehlt: s.filter((x) => x === 'fehlt' || x === 'tot').length,
      neu: s.filter((x) => x === 'neu').length, fehler: s.filter((x) => x === 'fehler').length,
      aenderungen: aend.reduce((a, b) => a + b, 0), bereiche: aend.filter(Boolean).length, gesamt: ids.length,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ctx entsteht je Zeichnen neu, seine Teile stehen in den Abhängigkeiten
  }, [d, ids, fehler, scope, plattform]);
  const sctx = { ...ctx, fehler };
  const turnier = scope === 'std' ? null : turniere.find((t) => t.turnier_id === scope);
  const geaendertListe = () => ids.filter((id) => geaenderteFelder(id, d).length).map((id) => `${titelVon(id)}: ${geaenderteFelder(id, d).join(', ')}`);

  /* ═══ Ändern ═══ */
  const setZp = (pfad, v) => setD((alt) => {
    const n = klon(alt);
    if (typeof pfad === 'string') n.zp.werte[pfad] = v;
    else {
      const [, k, feld, unter] = pfad;
      const disc = n.zp.werte.disciplines;
      // Neue Zeile (K1, Fullcontact, Continous Fighting): mit den Werten der
      // Alt-Zeile „Kickboxen“ beginnen, mit denen der Zeitplan bisher rechnete
      // (lib/kampfRegeln.js rechnet zeilenweise, nicht feldweise)
      if (!disc[k]) { const alt = (ALT_ZEILEN[k] || []).find((a) => disc[a]); disc[k] = alt ? klon(disc[alt]) : {}; }
      if (unter) { disc[k][unter] = disc[k][unter] || {}; disc[k][unter][feld] = v; } else disc[k][feld] = v;
    }
    return n;
  });
  const setZpOption = (feld, v) => setD((alt) => {
    const n = klon(alt);
    n.zp.werte.disciplines._optionen = { ...(n.zp.werte.disciplines._optionen || {}), [feld]: v };
    return n;
  });
  const setWertung = (typ, feld, v) => setD((alt) => { const n = klon(alt); n.wertung.werte[typ][feld] = v; return n; });
  const setVorlage = (k, v) => setD((alt) => { const n = klon(alt); n.vorlagen.werte[k] = v; return n; });
  const verwerfen = () => {
    setD((alt) => ({ ...klon(alt), zp: { ...alt.zp, werte: klon(alt.zp.basis) }, wertung: { ...alt.wertung, werte: klon(alt.wertung.basis) }, vorlagen: alt.vorlagen ? { ...alt.vorlagen, werte: { ...alt.vorlagen.basis } } : null }));
    setSpeicherFehler('');
  };

  /* ═══ Speichern ═══ */
  const zpGeaendert = () => ids.some((id) => id.startsWith('z:') && geaenderteFelder(id, d).length);
  const zpSenden = async (stand) => {
    const url = scope === 'std' ? '/api/schedule/zeitplan-einstellungen/default' : `/api/schedule/zeitplan-einstellungen/${scope}`;
    const body = { start_time: d.zp.werte.start_time, pause_between_categories: Number(d.zp.werte.pause_between_categories), disciplines: d.zp.werte.disciplines, stand };
    const token = localStorage.getItem('token');
    const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body) });
    const j = await res.json().catch(() => ({}));
    if (res.status === 409 && j.code === 'KONFLIKT') return { konflikt: j.aktuell };
    if (!res.ok || j.success === false) throw new Error(j.error || `Fehler ${res.status}`);
    return { daten: j.data };
  };

  const speichern = async (standErzwingen) => {
    if (!d) return false;
    const f = Object.keys(fehler);
    if (f.length) {
      const [id] = f[0].split('|');
      setSel(id); setOffen((o) => new Set(o).add(gruppeVon(id))); setHd('detail');
      setTimeout(() => document.querySelector(`[data-feld="${f[0]}"]`)?.focus(), 30);
      toast.fehler('Erst die Eingabe prüfen');
      return false;
    }
    setSpeichert(true); setSpeicherFehler('');
    try {
      let n = klon(d);
      if (zpGeaendert()) {
        const r = await zpSenden(standErzwingen !== undefined ? standErzwingen : d.zp.stand);
        if (r.konflikt !== undefined) { setDialog({ art: 'konflikt', aktuell: r.konflikt }); return false; }
        const z = zeitplanAus(r.daten);
        n.zp = { ...n.zp, werte: klon(z), basis: klon(z), stand: r.daten?.stand ?? null, quelle: scope === 'std' ? 'standard' : 'turnier', geaendert: r.daten?.updated_at || null };
        if (scope === 'std') setVorgabe(z); else setEigene((e) => new Set(e).add(scope));
      }
      for (const typ of Object.keys(n.wertung.werte)) {
        if (!geaenderteFelder(`w:${typ}`, d).length) continue;
        const w = n.wertung.werte[typ];
        const body = { ...w, auto_dq_ab_verwarnung: w.auto_dq_ab_verwarnung === '' ? null : w.auto_dq_ab_verwarnung };
        await apiJson(`/api/wertung/einstellungen/${encodeURIComponent(typ)}`, { method: 'PUT', body: JSON.stringify(body) });
        n.wertung.basis[typ] = klon(w);
      }
      if (n.vorlagen && VORLAGEN.some((v) => geaenderteFelder(`v:${v.k}`, d).length)) {
        const r = await apiJson('/api/ausschreibung/vorlagen', { method: 'PUT', body: JSON.stringify({ ...n.vorlagen.werte, ...(plattform ? { verband_id: Number(verband) } : {}) }) });
        const t = Object.fromEntries(VORLAGEN.map((v) => [v.k, r.data?.[v.k] || '']));
        n.vorlagen = { ...n.vorlagen, werte: { ...t }, basis: { ...t } };
      }
      setD(n);
      toast.ok(scope === 'std' ? 'Vorgaben gespeichert' : `Gespeichert für ${turnier?.name || 'das Turnier'}`);
      return true;
    } catch (e) {
      setSpeicherFehler(e.message || 'Der Server antwortet nicht');
      return false;
    } finally { setSpeichert(false); }
  };

  /* ═══ Wechsel mit Rückfrage ═══ */
  const ausfuehren = useCallback(async (ziel) => {
    if (ziel.typ === 'scope') { await ladeScope(ziel.wert, verband); }
    else if (ziel.typ === 'verband') { setVerband(ziel.wert); const v = await ladeVorlagen(ziel.wert); setD((alt) => ({ ...alt, vorlagen: v })); }
    else if (ziel.typ === 'pfad') { frei.current = true; navigate(ziel.wert); }
    else if (ziel.typ === 'klick') { frei.current = true; ziel.el.click(); setTimeout(() => { frei.current = false; }, 0); }
    else if (ziel.typ === 'bereich') { setSel(ziel.wert); setHd('detail'); }
  }, [ladeScope, ladeVorlagen, navigate, verband]);

  const mitRueckfrage = (ziel) => {
    if (zahlen.aenderungen) { setDialog({ art: 'verlassen', ziel }); return; }
    ausfuehren(ziel);
  };
  const waehle = (id) => {
    if (!id) return;
    setOffen((o) => new Set(o).add(gruppeVon(id)));
    if (id !== sel && geaenderteFelder(sel, d).length) { setDialog({ art: 'bereich', ziel: { typ: 'bereich', wert: id, name: titelVon(id) } }); return; }
    setSel(id); setHd('detail');
    if (window.matchMedia('(max-width: 760px)').matches) setTimeout(() => wurzel.current?.querySelector('.te-gilt')?.scrollIntoView({ block: 'start' }), 30);
  };
  const zumTurnier = (reiter) => mitRueckfrage({ typ: 'pfad', wert: `/dashboard/turnier-bearbeiten/${scope}?reiter=${reiter}`, name: `${turnier?.name} › ${reiter === 'einstellungen' ? 'Einstellungen' : 'Ausschreibung'}` });

  // Verlassen der Seite: Neu laden / Fenster schließen …
  const offeneRef = useRef(0);
  offeneRef.current = zahlen.aenderungen;
  useEffect(() => {
    const vorEntladen = (e) => { if (offeneRef.current && !frei.current) { e.preventDefault(); e.returnValue = ''; } };
    // … und Klicks außerhalb der Seite, die wegführen
    const klick = (e) => {
      if (!offeneRef.current || frei.current) return;
      const el = e.target.closest?.(WEG);
      if (!el || wurzel.current?.contains(el)) return;
      e.preventDefault(); e.stopPropagation();
      setDialog({ art: 'verlassen', ziel: { typ: 'klick', el, name: (el.textContent || '').trim().slice(0, 40) || 'eine andere Seite' } });
    };
    window.addEventListener('beforeunload', vorEntladen);
    document.addEventListener('click', klick, true);
    return () => { window.removeEventListener('beforeunload', vorEntladen); document.removeEventListener('click', klick, true); };
  }, []);

  /* ═══ Zurücksetzen ═══ */
  const zuruecksetzen = async () => {
    try {
      await apiJson(`/api/schedule/zeitplan-einstellungen/${scope}`, { method: 'DELETE' });
      setEigene((e) => { const n = new Set(e); n.delete(scope); return n; });
      setDialog(null);
      await ladeScope(scope, verband);
      toast.ok('Zurückgesetzt, es gilt die Vorgabe');
    } catch (e) { toast.fehler(e.message); }
  };

  /* ═══ Zeichnen ═══ */
  if (laedt && !d) {
    return <div className="vv te" ref={wurzel}><div className="vv-kopf"><h1><span className="vv-strich" />Turnier-Einstellungen</h1></div>
      <div className="te-raster"><span className="vv-skel" style={{ height: 420 }} /><span className="vv-skel" style={{ height: 420 }} /><span className="vv-skel" style={{ height: 420 }} /></div></div>;
  }
  if (!d) {
    return <div className="vv te" ref={wurzel}><div className="vv-kopf"><h1><span className="vv-strich" />Turnier-Einstellungen</h1></div>
      <div className="vv-hinweis vv-hinweis-rot" role="alert"><Ico n="wolke" /><div><b>Nicht geladen</b>{ladeFehler}</div></div></div>;
  }

  const op = zahlen.fehlt + zahlen.fehler;
  const eigenText = `${eigene.size} von ${turniere.length}`;
  const spZahl = speicherFehler ? ['vv-rot', 'Nicht gespeichert', 'Server antwortet nicht']
    : zahlen.aenderungen ? ['te-blau', `${zahlen.aenderungen} ${zahlen.aenderungen === 1 ? 'Änderung' : 'Änderungen'}`, 'noch nicht gespeichert']
      : ['te-gruen', 'Gespeichert', d.zp.stand && scope !== 'std' ? `zuletzt ${zeitDe(d.zp.stand)}` : 'nichts offen'];

  const offenePunkte = (() => {
    const o = [];
    ids.filter((id) => fehlerVon(fehler, id).length).forEach((id) => o.push({ id, text: `Eingabe prüfen: ${titelVon(id)}`, c: 'te-c-fehler' }));
    if (scope === 'std' && d.zp.quelle !== 'standard') o.push({ id: BASIS_ID, text: 'Die Vorgabe für den Zeitplan wurde nie gespeichert; es gelten eingebaute Werte.', c: 'te-c-fehlt' });
    if (scope !== 'std' && Number(d.zp.matten) === 0) o.push({ id: BASIS_ID, text: 'Keine Matte angelegt: der Zeitplan hat keine Fläche.', c: 'te-c-fehlt' });
    const eingebaut = ids.filter((id) => id.startsWith('z:') && id !== BASIS_ID && status(id, d, sctx) === 'fehlt');
    if (eingebaut.length) o.push({ id: eingebaut[0], text: `${eingebaut.map(titelVon).join(', ')}: nicht vollständig gespeichert, es gilt der eingebaute Wert.`, c: 'te-c-fehlt' });
    const tot = ids.filter((id) => status(id, d, sctx) === 'tot');
    if (tot.length) o.push({ id: tot[0], text: `${tot.length} ${tot.length === 1 ? 'Zeile' : 'Zeilen'} ohne Wirkung: ${tot.map(titelVon).join(', ')}.`, c: 'te-c-fehlt' });
    const leer = VORLAGEN.filter((v) => d.vorlagen && !d.vorlagen.werte[v.k]);
    if (scope === 'std' && leer.length) o.push({ id: `v:${leer[0].k}`, text: `Ausschreibungsvorlagen${d.vorlagen?.verband_name ? ` des Verbands „${d.vorlagen.verband_name}“` : ''}: ${leer.length === 3 ? 'alle leer' : `${leer.map((v) => v.name).join(', ')} leer`}.`, c: 'te-c-fehlt' });
    return o;
  })();

  const n = ids.indexOf(sel);
  const selSicher = ids.includes(sel) ? sel : BASIS_ID;
  const verbandWahl = plattform ? { verbaende, verband, onVerband: (v) => (VORLAGEN.some((x) => geaenderteFelder(`v:${x.k}`, d).length) ? setDialog({ art: 'verlassen', ziel: { typ: 'verband', wert: v, name: verbaende.find((x) => String(x.id) === String(v))?.name } }) : ausfuehren({ typ: 'verband', wert: v })) } : null;

  return (
    <div className={`vv te te-hd-${hd}`} ref={wurzel}>
      <div className="vv-kopf">
        <div>
          <h1><span className="vv-strich" />Turnier-Einstellungen</h1>
          <p>Vorgaben für Wertung, Zeitplan und Ausschreibung. Ein Turnier übernimmt sie, solange es nichts Eigenes einstellt.</p>
        </div>
        <label className="te-gilt">Gilt für
          <select className="vv-wahl" value={String(scope)} onChange={(e) => { const v = e.target.value === 'std' ? 'std' : Number(e.target.value); mitRueckfrage({ typ: 'scope', wert: v, name: v === 'std' ? 'Vorgabe für alle Turniere' : turniere.find((t) => t.turnier_id === v)?.name }); }}>
            <option value="std">Vorgabe für alle Turniere</option>
            {turniere.map((t) => <option key={t.turnier_id} value={t.turnier_id}>{t.name} ({datumDe(t.datum)})</option>)}
          </select>
        </label>
      </div>

      <div className="vv-zahlen">
        <button type="button" className="vv-zahl" aria-pressed={filter === 'alle'} onClick={() => setFilter('alle')}><small>Vollständig</small><b style={{ color: 'var(--vv-gruen)' }}>{zahlen.ok} von {zahlen.gesamt}</b><span>Bereiche gesetzt oder übernommen</span></button>
        <button type="button" className={`vv-zahl${op ? ' vv-warn' : ''}`} aria-pressed={filter === 'offen'} onClick={() => setFilter((f) => (f === 'offen' ? 'alle' : 'offen'))}><small>Offene Punkte</small><b>{op}</b><span>nicht gespeichert, leer, ohne Wirkung</span></button>
        <div className="vv-zahl"><small>Eigene Turnierwerte</small><b>{eigenText}</b><span>Turniere mit eigenem Zeitplan</span></div>
        <button type="button" className="vv-zahl" aria-pressed={filter === 'neu'} onClick={() => setFilter((f) => (f === 'neu' ? 'alle' : 'neu'))}><small>Speichern</small>
          <b style={{ color: spZahl[0] === 'vv-rot' ? 'var(--vv-rot)' : spZahl[0] === 'te-blau' ? 'var(--vv-blau)' : 'var(--vv-gruen)' }}>{spZahl[1]}</b><span>{spZahl[2]}</span></button>
      </div>

      <div className={`te-raster te-hd-${hd}`}>
        <Gruppenliste gruppen={gr} d={d} ctx={sctx} offen={offen} zahlen={zahlen}
          onGruppe={(k) => { setOffen((o) => { const x = new Set(o); if (x.has(k)) x.delete(k); else x.add(k); return x; }); setFilter('alle'); }}
          filter={filter} onFilter={setFilter} sel={selSicher} onWaehle={waehle} onLink={zumTurnier} />

        <div className="te-e-spalte">
          <button type="button" className="vv-k vv-k-leise te-zurueck" onClick={() => setHd('liste')}><Ico n="links" /> Alle Bereiche</button>
          <section className="te-editor" aria-label="Bearbeiten" aria-busy={laedt}>
            <EditorKopf id={selSicher} d={d} ctx={sctx} />
            <div className="te-e-inhalt">
              {speicherFehler && (
                <div className="vv-hinweis vv-hinweis-rot" role="alert"><Ico n="wolke" /><div><b>Nicht gespeichert</b>{speicherFehler}. Deine Eingaben bleiben stehen.</div>
                  <button type="button" className="vv-k vv-k-klein" onClick={() => speichern()}>Erneut speichern</button></div>
              )}
              <EditorInhalt id={selSicher} d={d} ctx={sctx} vorgabe={vorgabe} fehler={fehler}
                setZp={setZp} setZpOption={setZpOption} setWertung={setWertung} setVorlage={setVorlage}
                onWaehle={waehle} onMatten={() => mitRueckfrage({ typ: 'pfad', wert: '/dashboard/kampfflaechen', name: 'Mattenverwaltung' })}
                verbandWahl={verbandWahl} />
            </div>
            <div className="te-e-fuss">
              <div className="te-nav">
                <button type="button" className="vv-k vv-k-klein vv-k-leise" disabled={n <= 0} onClick={() => waehle(ids[n - 1])}><Ico n="links" /> Vorheriger</button>
                <button type="button" className="vv-k vv-k-klein vv-k-leise" disabled={n >= ids.length - 1} onClick={() => waehle(ids[n + 1])}>Nächster <Ico n="rechts" /></button>
              </div>
              <small>Alle Bereiche werden zusammen gespeichert</small>
            </div>
          </section>
        </div>

        <aside className="vv-leiste te-leiste" aria-label="Zusammenfassung">
          <div className="vv-l-kopf">
            <span className="vv-siegel" aria-hidden="true">{scope === 'std' ? '設' : '試'}</span>
            <div>
              <h2>{scope === 'std' ? 'Vorgabe für alle Turniere' : turnier?.name}</h2>
              <div className="vv-unter">{scope === 'std'
                ? (plattform ? `alle Verbände · ${turniere.length} Turniere` : `${d.vorlagen?.verband_name || 'dein Verband'} · ${turniere.length} Turniere`)
                : `${datumDe(turnier?.datum)} · ${STATUS_TURNIER[turnier?.status] || turnier?.status || ''} · ${d.zp.matten} ${Number(d.zp.matten) === 1 ? 'Matte' : 'Matten'}`}</div>
              {scope !== 'std' && <div className="vv-unter">{d.zp.quelle === 'turnier' ? <span className="vv-st te-st-ok">eigene Zeitplanwerte</span> : <span className="vv-st te-st-erbt">wie Vorgabe</span>}</div>}
            </div>
          </div>
          <div className="vv-l-zahlen">
            <div><b>{zahlen.ok}</b><small>gesetzt</small></div>
            <div><b style={{ color: 'var(--vv-gelb)' }}>{zahlen.fehlt}</b><small>offen</small></div>
            <div><b style={{ color: 'var(--vv-blau)' }}>{zahlen.neu + zahlen.fehler}</b><small>geändert</small></div>
          </div>
          <div className="te-l-speichern">
            {speicherFehler
              ? <div className="te-zustand te-z-fehler"><Ico n="wolke" /><div><b>Nicht gespeichert</b><small>Erneut versuchen, die Eingaben bleiben.</small></div></div>
              : zahlen.aenderungen
                ? <div className="te-zustand te-z-neu"><Ico n="uhr" /><div><b>{zahlen.aenderungen} {zahlen.aenderungen === 1 ? 'Änderung' : 'Änderungen'} offen</b><small>in {zahlen.bereiche} {zahlen.bereiche === 1 ? 'Bereich' : 'Bereichen'}{zahlen.fehler ? `, davon ${zahlen.fehler} mit Fehler` : ''}</small></div></div>
                : <div className="te-zustand te-z-ok"><Ico n="haken" /><div><b>Alles gespeichert</b><small>{d.zp.stand && scope !== 'std' ? `zuletzt ${zeitDe(d.zp.stand)}` : 'keine offenen Änderungen'}</small></div></div>}
            <div className="te-reihe">
              <button type="button" className="vv-k" disabled={!zahlen.aenderungen || speichert} onClick={verwerfen}>Verwerfen</button>
              <button type="button" className="vv-k vv-k-haupt" disabled={!zahlen.aenderungen || speichert} onClick={() => speichern()}>{speichert ? 'Speichert …' : 'Speichern'}</button>
            </div>
            {scope !== 'std' && (
              <button type="button" className="vv-k vv-k-leise vv-k-klein" disabled={d.zp.quelle !== 'turnier'} onClick={() => setDialog({ art: 'zurueck' })}><Ico n="zurueck" /> Auf Vorgabe zurücksetzen</button>
            )}
          </div>
          {offenePunkte.length > 0 && (
            <div className="vv-l-teil"><h3>Offene Punkte</h3>
              <ul className="te-offen-liste">{offenePunkte.map((o) => (
                <li key={o.text}><button type="button" onClick={() => waehle(o.id)}><span className={`te-punkt ${o.c}`} /><span>{o.text}</span></button></li>
              ))}</ul>
            </div>
          )}
          <div className="vv-l-teil"><h3>Wirkt auf</h3>
            <ul className="te-wirkt">
              <li><Ico n="uhr" /><span>Zeitplan: Dauer je Kategorie und Beginn</span></li>
              <li><Ico n="leute" /><span>Formen und Selbstverteidigung: Kampfrichter bzw. Übungen neu angelegter Kategorien</span></li>
              <li><Ico n="baum" /><span>Brackets: K.O.-Baum ja oder nein, automatische Disqualifikation</span></li>
              <li><Ico n="dok" /><span>Ausschreibung: Vorschlag für Haftung, Versicherung, Datenschutz</span></li>
            </ul>
          </div>
          <div className="te-l-recht"><Ico n="schloss" /><span>{plattform
            ? 'Plattform-Admin: du änderst die Vorgaben für Wertung und Zeitplan für alle Verbände.'
            : 'Du änderst den Zeitplan deiner Turniere und die Ausschreibungsvorlagen deines Verbands. Die Vorgaben für Wertung und Zeitplan gelten für alle Verbände und sind für dich gesperrt.'}</span></div>
        </aside>
      </div>

      {zahlen.aenderungen > 0 && (
        <div className="te-speicherleiste">
          <b>{zahlen.aenderungen} offen</b>
          <button type="button" className="vv-k vv-k-klein" onClick={verwerfen}>Verwerfen</button>
          <button type="button" className="vv-k vv-k-klein vv-k-haupt" disabled={speichert || zahlen.fehler > 0} onClick={() => speichern()}>Speichern</button>
        </div>
      )}

      {dialog?.art === 'verlassen' && (
        <Dialog sym="achtung" titel={`${zahlen.aenderungen} ${zahlen.aenderungen === 1 ? 'Änderung' : 'Änderungen'} nicht gespeichert`}
          text={`Du wechselst zu „${dialog.ziel.name || ''}“. Was soll mit den Eingaben passieren?`}
          onSchliessen={() => setDialog(null)} belegt={speichert}
          fuss={<>
            <button type="button" className="vv-k vv-k-leise" onClick={() => setDialog(null)}>Hierbleiben</button>
            <button type="button" className="vv-k vv-k-gefahr" onClick={() => { const z = dialog.ziel; setDialog(null); verwerfen(); ausfuehren(z); }}>Verwerfen und wechseln</button>
            <button type="button" className="vv-k vv-k-haupt" disabled={zahlen.fehler > 0 || speichert} title={zahlen.fehler ? 'Erst den Fehler beheben' : undefined}
              onClick={async () => { const z = dialog.ziel; setDialog(null); if (await speichern()) ausfuehren(z); }}>Speichern und wechseln</button>
          </>}>
          <ul>{geaendertListe().map((t) => <li key={t}>{t}</li>)}</ul>
        </Dialog>
      )}
      {dialog?.art === 'bereich' && (
        <Dialog sym="achtung" titel={`${titelVon(sel)}: nicht gespeichert`}
          text={`Du wechselst zu „${dialog.ziel.name}“. Die Änderung kann bleiben und wird mit den anderen zusammen gespeichert.`}
          onSchliessen={() => setDialog(null)} belegt={speichert}
          fuss={<>
            <button type="button" className="vv-k vv-k-leise" onClick={() => setDialog(null)}>Hierbleiben</button>
            <button type="button" className="vv-k" onClick={() => { const z = dialog.ziel; setDialog(null); ausfuehren(z); }}>Behalten und wechseln</button>
            <button type="button" className="vv-k vv-k-haupt" disabled={zahlen.fehler > 0 || speichert}
              onClick={async () => { const z = dialog.ziel; setDialog(null); if (await speichern()) ausfuehren(z); }}>Speichern und wechseln</button>
          </>}>
          <ul>{geaenderteFelder(sel, d).map((t) => <li key={t}>{t}</li>)}</ul>
        </Dialog>
      )}
      {dialog?.art === 'konflikt' && (() => {
        const jetzt = dialog.aktuell ? zeitplanAus(dialog.aktuell) : vorgabe || zeitplanAus(null);
        const zeilen = zeitplanUnterschiede(d.zp.werte, jetzt);
        return (
          <Dialog sym="leute" titel="Inzwischen anders gespeichert"
            text={`Während du bearbeitet hast, hat jemand anderes ${scope === 'std' ? 'die Vorgabe' : 'dieses Turnier'} ${dialog.aktuell ? `gespeichert (${zeitDe(dialog.aktuell.stand)})` : 'auf die Vorgabe zurückgesetzt'}.`}
            onSchliessen={() => setDialog(null)} belegt={speichert} breit
            fuss={<>
              <button type="button" className="vv-k" onClick={async () => { setDialog(null); await ladeScope(scope, verband); toast.info('Neu geladen'); }}>Neu laden, meine verwerfen</button>
              <button type="button" className="vv-k vv-k-haupt" onClick={() => { const s = dialog.aktuell ? dialog.aktuell.stand : null; setDialog(null); speichern(s); }}>Meine Werte speichern</button>
            </>}>
            {zeilen.length
              ? <table className="te-vergleich"><thead><tr><th>Feld</th><th>Deine Eingabe</th><th>Jetzt gespeichert</th></tr></thead>
                <tbody>{zeilen.map(([f, a, b]) => <tr key={f}><td>{f}</td><td className="te-mein">{a}</td><td className="te-server">{b}</td></tr>)}</tbody></table>
              : <p className="vv-dialog-notiz">Die sichtbaren Werte sind gleich; geändert hat sich ein Feld ohne Wirkung.</p>}
            <p className="vv-dialog-notiz">Nichts wird still überschrieben.</p>
          </Dialog>
        );
      })()}
      {dialog?.art === 'zurueck' && (() => {
        const zeilen = vorgabe ? zeitplanUnterschiede(d.zp.basis, vorgabe) : [];
        return (
          <Dialog sym="neu" rot titel={`${turnier?.name} auf Vorgabe zurücksetzen?`}
            text="Die eigenen Zeitplanwerte dieses Turniers werden gelöscht. Danach gilt die Vorgabe für alle Turniere."
            onSchliessen={() => setDialog(null)}
            fuss={<>
              <button type="button" className="vv-k vv-k-leise" onClick={() => setDialog(null)}>Abbrechen</button>
              <button type="button" className="vv-k vv-k-haupt" onClick={zuruecksetzen}>Zurücksetzen</button>
            </>}>
            <ul>
              {zeilen.length ? zeilen.map(([f, a, b]) => <li key={f}>{f}: {a} wird {b}</li>) : <li>Die Werte sind gleich wie die Vorgabe.</li>}
              {zahlen.aenderungen > 0 && <li>Deine {zahlen.aenderungen} ungespeicherten Änderungen gehen verloren.</li>}
              <li>Ein bereits erzeugter Zeitplan bleibt, bis du ihn neu berechnest.</li>
            </ul>
          </Dialog>
        );
      })()}
    </div>
  );
}
